import Config from '@config';
import { CACHE_EXPIRATION_TIME } from '@gasbi/shared/common/features/constants';
import { initializeEmployeesApi } from '@gasbi/shared/common/features/employees';

import { injectCrmSlice } from '@/app/crmApi';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import type {
  AssignableEmployeeReadModel,
  EmployeeModel,
  GetEmployeesByIdentityIdParams,
} from './types';

export const {
  employeesApi,
  useGetEmployeesByRoleQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeByIdMutation,
  useActivateEmployeeByIdMutation,
  useResetEmployeePasswordByIdMutation,
  useDeactivateEmployeeByIdMutation,
  useLazyGetEmployeeByEmailQuery,
  useGetEmployeeByIdentityDetailsQuery,
  useActivateEmployeeByIdentityIdMutation,
  useResetPasswordEmployeeByIdentityIdMutation,
  useDeactivateEmployeeByIdentityIdMutation,
  useGetEmployeesInfiniteQuery,
  useGetEmployeesByIdentityInfiniteQuery,
  useGetEmployeesByRoleInfiniteQuery,
  useGetEmployeesAllInfiniteQuery,
  useGetEmployeesAccessibleByOrganizationQuery,
  useGetEmployeeByIdentityIdSelector,
} = initializeEmployeesApi({
  baseUrl: Config.VITE_API_URL,
  baseQuery: axiosBaseQuery,
});

export const employeesLocalApi = injectCrmSlice({
  baseUrl: Config.VITE_API_URL,
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getAssignableEmployees: builder.query<AssignableEmployeeReadModel[], void>({
      query: () => ({
        url: '/employees/assignable',
      }),
    }),
    getEmployeesByIdentityId: builder.query<
      EmployeeModel[],
      GetEmployeesByIdentityIdParams
    >({
      query: ({ identityId, page, pageSize, orderBy, filter }) => ({
        url: '/employees/by-identity-id',
        params: { identityId, page, pageSize, orderBy, filter },
      }),
    }),
  }),
});

export const {
  useGetAssignableEmployeesQuery,
  useGetEmployeesByIdentityIdQuery,
} = employeesLocalApi;

const employeesApiWithCrmTransforms = employeesApi.injectEndpoints({
  overrideExisting: true,
  endpoints: builder => ({
    getEmployeeById: builder.query<EmployeeModel, string>({
      query: id => ({ url: `/${id}` }),
      transformResponse: (data: EmployeeModel) => {
        return {
          ...data,
          roles: data?.roles?.map(({ roleId, roleName }) => ({
            id: roleId,
            name: roleName,
          })),
        } as unknown as EmployeeModel;
      },
      providesTags: (_, __, id) => [{ type: 'Employee', id }],
    }),
  }),
});

export const { useGetEmployeeByIdQuery } = employeesApiWithCrmTransforms;
